import { useState, useEffect, useMemo } from "react"
import Modal from "aldoo-ra/Modal"
import Input from "aldoo-ra/Input"
import Button from "aldoo-ra/Button"
import Typography from "aldoo-ra/Typography"
import TextBundle from "aldoo-ra/CMS/text-bundle"
import { UserPlusIcon, MagnifyingGlassIcon } from "@heroicons/react/24/outline"
import { url as cmsUrl } from "aldoo-ra/CMS/url"

const AVATAR_COLORS = [
  "#e53935", "#d81b60", "#8e24aa", "#5e35b1",
  "#1e88e5", "#039be5", "#00897b", "#43a047",
  "#fb8c00", "#f4511e", "#6d4c41", "#546e7a",
]

function getAvatarColor(str) {
  if (!str) return AVATAR_COLORS[0]
  let hash = 0
  for (let i = 0; i < str.length; i++) hash = str.charCodeAt(i) + ((hash << 5) - hash)
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length]
}

function CandidateAvatar({ person }) {
  const [imgError, setImgError] = useState(false)
  const avatarSrc = person.avatar && !imgError ? cmsUrl(person.avatar) : null
  const initial = (person.name || "?")[0].toUpperCase()
  const color = getAvatarColor(person.userId || person.name)

  if (avatarSrc) {
    return (
      <img
        src={avatarSrc}
        alt=""
        onError={() => setImgError(true)}
        className="w-9 h-9 rounded-full object-cover flex-shrink-0 border border-bordered"
      />
    )
  }

  return (
    <div
      className="w-9 h-9 rounded-full flex-shrink-0 flex items-center justify-center text-white font-semibold text-sm"
      style={{ background: color }}
    >
      {initial}
    </div>
  )
}

function ManualTab({ onAdd }) {
  const texts = TextBundle({ id: "transfer_contacts" })
  const [name, setName] = useState("")
  const [email, setEmail] = useState("")
  const [saving, setSaving] = useState(false)
  const [success, setSuccess] = useState(false)

  const canSave = name.trim().length > 0

  const handleSave = async () => {
    if (!canSave) return
    setSaving(true)
    setSuccess(false)
    const trimmedEmail = email.trim()
    const userId = trimmedEmail ? `email:${trimmedEmail}` : `manual:${Date.now()}-${Math.random().toString(36).slice(2)}`
    try {
      await onAdd({ userId, name: name.trim(), email: trimmedEmail || null, avatar: null })
      setName("")
      setEmail("")
      setSuccess(true)
      setTimeout(() => setSuccess(false), 2500)
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="flex-1 flex flex-col gap-4 px-5 py-5">
      <div className="flex flex-col">
        <Typography variant="label_upper_dim">
          {texts?.manualNameLabel}
        </Typography>

        <Input
          variant="meet_compact"
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && handleSave()}
          placeholder={texts?.manualNamePlaceholder}
          autoFocus
          className="w-full"
        />
      </div>

      <div className="flex flex-col">
        <Typography variant="label_upper_dim">
          {texts?.manualEmailLabel}
        </Typography>

        <Input
          variant="meet_compact"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && handleSave()}
          placeholder={texts?.manualEmailPlaceholder}
          className="w-full"
        />
      </div>

      {success && (
        <Typography variant="txt2_accent" className="text-success">
          {texts?.manualContactAdded}
        </Typography>
      )}

      <Button
        variant="primary"
        onClick={handleSave}
        disabled={!canSave || saving}
        className="w-full"
      >
        {saving ? (
          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
        ) : (
          <UserPlusIcon className="w-4 h-4" />
        )}
        {texts?.addContact}
      </Button>
    </div>
  )
}

export default function AddContactDialog({ existingContactIds, currentUserId, onAdd, onClose }) {
  const texts = TextBundle({ id: "transfer_contacts" })

  return (
    <Modal
      variant="meet_compact"
      isOpen={true}
      onClose={onClose}
      allowCloseBtn={true}
      title={texts?.addContact}
    >
      <ManualTab onAdd={onAdd} />
    </Modal>
  )
}
